import { createClient } from '@supabase/supabase-js';
export type { User, Session } from '@supabase/supabase-js';
import type { User } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ── Auth state cache (for synchronous auth.currentUser access) ────────────────
let _currentUser: User | null = null;

supabase.auth.onAuthStateChange((_, session) => {
  _currentUser = session?.user ?? null;
});

// ── Firebase-compatible auth object ──────────────────────────────────────────
export const auth = {
  get currentUser(): User | null {
    return _currentUser;
  },
  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      callback(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  },
};

// Firebase-compatible standalone onAuthStateChanged
export const onAuthStateChanged = (
  _auth: typeof auth,
  callback: (user: User | null) => void,
): () => void => auth.onAuthStateChanged(callback);

// ── Auth helpers ──────────────────────────────────────────────────────────────

export const signInWithGoogle = async (): Promise<void> => {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.origin },
  });
  if (error) throw error;
};

export const signUpWithEmail = async (email: string, password: string): Promise<User | null> => {
  const { data, error } = await supabase.auth.signUp({ email, password });
  if (error) throw error;
  return data.user;
};

export const signInWithEmail = async (email: string, password: string): Promise<User | null> => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data.user;
};

export const sendMagicLink = async (email: string): Promise<void> => {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: window.location.origin },
  });
  if (error) throw error;
};

export const logoutUser = async (): Promise<void> => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};
